/** @format */

import React, { useContext, useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import { useTranslation } from "react-i18next";
import { roles } from "../../Config/Roles";
import { AuthContext } from "../../Contexts/AuthContext";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { surveyService } from "../../Services/survey.services";
import ActionModal from "../Common/ActionModal";
import ResultModal from "../Common/ResultModal";
import SpinnerLoading from "../Common/SpinnerLoading";
import AddSurvey from "./AddSurvey";
import UpdateSurvey from "./UpdateSurvey";

const Index = () => {
  const { t: translator } = useTranslation();
  const { updateLocName } = useContext(GlobalContext);
  const { roleId } = useContext(AuthContext);
  const { master, admin, support } = roles;

  const isActionAllowed = [master, admin, support].includes(parseInt(roleId));

  const { SearchBar } = Search;

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [updateData, setUpdateData] = useState(null);
  const [update, setUpdate] = useState(new Date());

  const [resultModal, setResultModal] = useState({
    show: false,
    title: "",
    text: "",
    btnClass: "",
  });

  const [actionModal, setActionModal] = useState({
    show: false,
    title: "",
    text: "",
    confirmBtn: "",
  });

  useEffect(() => updateLocName("surveys"), []);
  useEffect(() => {
    setIsLoading(true);
    surveyService
      .getAll()
      .then((res) => {
        setData(res.data);
        setIsLoading(false);
      })
      .catch((err) => console.log(err));
  }, [update]);

  const onClickDelete = (rowId) => {
    setActionModal({
      show: true,
      title: "Survey-Delete-Title",
      text: "Survey-Delete-Text",
      confirmBtn: "Survey-Delete-Btn",
      confirmAction: () => confirmDelete(rowId),
    });
  };

  function dateFormatter(cell) {
    if (!cell) return "";
    return new Intl.DateTimeFormat("hr-HR").format(new Date(cell));
  }

  function actionFormatter(rowId) {
    return (
      <div
        className="action"
        onClick={(e) => {
          e.stopPropagation();
          onClickDelete(rowId);
        }}
      >
        <span className="icon icon-cancel" style={{ color: "#c00" }}></span>
      </div>
    );
  }

  const columns = [
    {
      dataField: "surveyId",
      text: "",
      headerStyle: () => ({ width: "50px", textAlign: "center" }),
      formatter: actionFormatter,
      hidden: !isActionAllowed,
    },
    {
      dataField: "name",
      text: translator("Survey-Name"),
    },
    {
      dataField: "validFrom",
      text: translator("Survey-ValidFrom"),
      headerStyle: () => ({ width: "130px" }),
      formatter: dateFormatter,
    },
    {
      dataField: "validTo",
      text: translator("Survey-ValidTo"),
      headerStyle: () => ({ width: "130px" }),
      formatter: dateFormatter,
    },
  ];

  const rowEvents = {
    onClick: (e, row) => {
      e.stopPropagation();
      setUpdateData(row);
    },
  };

  const confirmDelete = (id) => {
    surveyService
      .remove(id)
      .then(() => {
        setActionModal({ ...actionModal, show: false });
        setResultModal({
          ...resultModal,
          title: translator("Survey-ResultModal-DeleteSuccess-Title"),
          text: translator("Survey-ResultModal-DeleteSuccess-Text"),
          show: true,
          btnClass: "btn btn-primary",
        });
        setUpdate(new Date());
      })
      .catch(() =>
        setResultModal({
          ...resultModal,
          title: translator("Survey-ResultModal-DeleteSuccess-Title"),
          text: translator("Survey-ResultModal-DeleteUnSuccess-Text"),
          show: true,
          btnClass: "btn btn-primary",
        })
      );
  };

  return (
    <div className="p-2 pt-3">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("Survey-View-Subtitle")}</span>
        </div>
        <div className="card-body">
          <ToolkitProvider
            keyField="surveyId"
            data={data}
            columns={columns}
            search
          >
            {(props) => (
              <>
                <div className="row">
                  <div className="col-md-6 col-sm-12 mt-2" onClick={() => setUpdateData(null)}>
                    {isLoading ? (
                      <div className="dashboard-card-spinner">
                        <SpinnerLoading />
                      </div>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-end">
                          <div>
                            <SearchBar {...props.searchProps} />
                          </div>
                        </div>
                        <BootstrapTable
                          {...props.baseProps}
                          rowClasses="boostrap-table-row"
                          rowEvents={rowEvents}
                          noDataIndication={translator("No-Data")}
                          pagination={paginationFactory({})}
                        />
                      </>
                    )}
                  </div>

                  <div className="col-md-6 col-sm-12 mt-2">
                    {!updateData && (
                      <AddSurvey
                        onSuccess={() => {
                          setUpdate(new Date());
                          setUpdateData(null);
                        }}
                        disabled={!isActionAllowed}
                        setResultModal={setResultModal}
                      />
                    )}
                    {updateData && (
                      <UpdateSurvey
                        updateData={updateData}
                        disabled={!isActionAllowed}
                        onSuccess={() => {
                          setUpdate(new Date());
                          setUpdateData(null);
                        }}
                        setResultModal={setResultModal}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </ToolkitProvider>
        </div>
      </div>

      <ResultModal
        show={resultModal.show}
        title={resultModal.title}
        text={resultModal.text}
        btnClass={resultModal.btnClass}
        handleClose={() => {
          setResultModal({ ...resultModal, show: false });
          setUpdateData(null);
        }}
      />

      <ActionModal
        show={actionModal.show}
        handleHide={() => {
          setActionModal({ ...actionModal, show: false });
        }}
        title={actionModal.title}
        text={actionModal.text}
        confirmBtn={actionModal.confirmBtn}
        confirmAction={actionModal.confirmAction}
      />
    </div>
  );
};

export default Index;
